import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Building2, Plus, MapPin, Users, Phone, Pencil, Trash2, Loader2, Wallet } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { CURRENCY_SYMBOL } from "@/lib/currency";

interface Props {
  nurseryId: string;
  nurseryName: string;
}

type PricingModel = "fixed" | "per_child";

const BranchesSection = ({ nurseryId, nurseryName }: Props) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editBranch, setEditBranch] = useState<any>(null);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [pricingModel, setPricingModel] = useState<PricingModel>("fixed");
  const [amount, setAmount] = useState<string>("0");
  const [pricePerChild, setPricePerChild] = useState<string>("0");

  const { data: branches = [], isLoading } = useQuery({
    queryKey: ["nursery-branches", nurseryId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("nurseries")
        .select("id, name, address, phone")
        .eq("parent_nursery_id", nurseryId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data || [];
    },
    enabled: !!nurseryId,
  });

  const branchIds = branches.map((b) => b.id);

  // Subscription per branch
  const { data: subsByBranch = {} } = useQuery<Record<string, any>>({
    queryKey: ["branch-subscriptions", branchIds],
    enabled: branchIds.length > 0,
    queryFn: async () => {
      const { data } = await supabase
        .from("nursery_subscriptions" as any)
        .select("nursery_id, pricing_model, amount, price_per_child")
        .in("nursery_id", branchIds)
        .eq("status", "active");
      const map: Record<string, any> = {};
      ((data as any[]) || []).forEach((s: any) => { map[s.nursery_id] = s; });
      return map;
    },
  });

  const { data: childrenCounts = {} } = useQuery<Record<string, number>>({
    queryKey: ["branch-children-counts", branchIds],
    enabled: branchIds.length > 0,
    queryFn: async () => {
      const counts: Record<string, number> = {};
      for (const id of branchIds) {
        const { count } = await supabase
          .from("children")
          .select("*", { count: "exact", head: true })
          .eq("nursery_id", id);
        counts[id] = count || 0;
      }
      return counts;
    },
  });

  const mutation = useMutation({
    mutationFn: async (action: string) => {
      const payload: any = {
        action,
        name,
        address,
        phone,
        pricing_model: pricingModel,
        amount: pricingModel === "fixed" ? Number(amount || 0) : 0,
        price_per_child: pricingModel === "per_child" ? Number(pricePerChild || 0) : null,
      };
      if (action === "update" && editBranch) payload.branchId = editBranch.id;
      const { data, error } = await supabase.functions.invoke("manage-branch", { body: payload });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      toast({ title: editBranch ? "تم تحديث الفرع بنجاح" : "تم إضافة الفرع بنجاح" });
      queryClient.invalidateQueries({ queryKey: ["nursery-branches"] });
      queryClient.invalidateQueries({ queryKey: ["branch-subscriptions"] });
      handleClose();
    },
    onError: (err: any) => {
      toast({ title: "خطأ", description: err.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (branchId: string) => {
      const { data, error } = await supabase.functions.invoke("manage-branch", {
        body: { action: "delete", branchId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      toast({ title: "تم حذف الفرع بنجاح" });
      queryClient.invalidateQueries({ queryKey: ["nursery-branches"] });
    },
    onError: (err: any) => {
      toast({ title: "خطأ", description: err.message, variant: "destructive" });
    },
  });

  const handleClose = () => {
    setDialogOpen(false);
    setEditBranch(null);
    setName(""); setAddress(""); setPhone("");
    setPricingModel("fixed"); setAmount("0"); setPricePerChild("0");
  };

  const handleEdit = (branch: any) => {
    const sub = subsByBranch[branch.id];
    setEditBranch(branch);
    setName(branch.name);
    setAddress(branch.address || "");
    setPhone(branch.phone || "");
    setPricingModel(sub?.pricing_model === "per_child" ? "per_child" : "fixed");
    setAmount(String(sub?.amount ?? 0));
    setPricePerChild(String(sub?.price_per_child ?? 0));
    setDialogOpen(true);
  };

  const branchMonthlyTotal = (branchId: string) => {
    const s = subsByBranch[branchId];
    if (!s) return 0;
    if (s.pricing_model === "per_child") {
      return Number(s.price_per_child || 0) * Number(childrenCounts[branchId] || 0);
    }
    return Number(s.amount || 0);
  };

  const totalAcrossBranches = branchIds.reduce((sum, id) => sum + branchMonthlyTotal(id), 0);

  return (
    <>
      <Card className="shadow-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            الفروع والفوترة الشهرية
          </CardTitle>
          <Button size="sm" onClick={() => setDialogOpen(true)} className="gap-1.5">
            <Plus className="w-4 h-4" />
            إضافة فرع
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Parent nursery */}
          <div className="flex items-center justify-between p-3 rounded-lg border bg-primary/5">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-primary" />
              <span className="font-medium">{nurseryName}</span>
              <Badge variant="secondary" className="text-xs">الرئيسي</Badge>
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : branches.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-3">
              لا توجد فروع — أضف فرعاً جديداً لحضانتك
            </p>
          ) : (
            branches.map((branch) => {
              const sub = subsByBranch[branch.id];
              const total = branchMonthlyTotal(branch.id);
              return (
                <div key={branch.id} className="p-3 rounded-lg border bg-muted/20 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <MapPin className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <span className="font-medium">{branch.name}</span>
                        {branch.address && (
                          <p className="text-xs text-muted-foreground">{branch.address}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(branch)}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => {
                          if (confirm("هل أنت متأكد من حذف هذا الفرع؟")) {
                            deleteMutation.mutate(branch.id);
                          }
                        }}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-xs pt-2 border-t">
                    <span className="flex items-center gap-1 text-muted-foreground">
                      <Users className="w-3.5 h-3.5" />
                      {childrenCounts[branch.id] ?? 0} طفل
                    </span>
                    <Badge variant="outline" className="text-xs">
                      {sub?.pricing_model === "per_child"
                        ? `حسب الأطفال × ${Number(sub.price_per_child || 0).toLocaleString("ar-EG")} ${CURRENCY_SYMBOL}`
                        : `مبلغ ثابت ${Number(sub?.amount || 0).toLocaleString("ar-EG")} ${CURRENCY_SYMBOL}`}
                    </Badge>
                    <span className="ms-auto flex items-center gap-1 font-bold text-primary">
                      <Wallet className="w-3.5 h-3.5" />
                      {total.toLocaleString("ar-EG")} {CURRENCY_SYMBOL} / شهر
                    </span>
                  </div>
                </div>
              );
            })
          )}

          {branches.length > 0 && (
            <div className="flex items-center justify-between p-3 mt-2 rounded-lg border-2 border-primary/30 bg-primary/5">
              <span className="font-bold">إجمالي الفروع شهرياً</span>
              <span className="font-bold text-lg text-primary">
                {totalAcrossBranches.toLocaleString("ar-EG")} {CURRENCY_SYMBOL}
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) handleClose(); else setDialogOpen(true); }}>
        <DialogContent dir="rtl">
          <DialogHeader>
            <DialogTitle>{editBranch ? "تعديل الفرع" : "إضافة فرع جديد"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>اسم الفرع *</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="مثال: فرع التجمع" />
            </div>
            <div className="space-y-2">
              <Label>العنوان</Label>
              <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="عنوان الفرع" />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5 text-muted-foreground" />
                رقم الهاتف
              </Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="01xxxxxxxxx" dir="ltr" className="text-right" />
            </div>

            <div className="space-y-2 pt-2 border-t">
              <Label className="flex items-center gap-1.5">
                <Wallet className="w-3.5 h-3.5 text-primary" />
                نموذج الفوترة الشهرية للفرع
              </Label>
              <RadioGroup
                value={pricingModel}
                onValueChange={(v) => setPricingModel(v as PricingModel)}
                className="grid grid-cols-2 gap-2"
              >
                <label className={`flex items-center gap-2 p-2 rounded border cursor-pointer ${pricingModel === "fixed" ? "border-primary bg-primary/5" : ""}`}>
                  <RadioGroupItem value="fixed" />
                  <span className="text-sm">مبلغ ثابت</span>
                </label>
                <label className={`flex items-center gap-2 p-2 rounded border cursor-pointer ${pricingModel === "per_child" ? "border-primary bg-primary/5" : ""}`}>
                  <RadioGroupItem value="per_child" />
                  <span className="text-sm">حسب عدد الأطفال</span>
                </label>
              </RadioGroup>

              {pricingModel === "fixed" ? (
                <div>
                  <Label className="text-xs">المبلغ الشهري ({CURRENCY_SYMBOL})</Label>
                  <Input
                    type="number"
                    min="0"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0"
                  />
                </div>
              ) : (
                <div>
                  <Label className="text-xs">سعر الطفل الواحد ({CURRENCY_SYMBOL})</Label>
                  <Input
                    type="number"
                    min="0"
                    value={pricePerChild}
                    onChange={(e) => setPricePerChild(e.target.value)}
                    placeholder="0"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    تُحتسب الفاتورة شهرياً = عدد الأطفال × السعر.
                  </p>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleClose}>إلغاء</Button>
            <Button onClick={() => mutation.mutate(editBranch ? "update" : "create")} disabled={!name.trim() || mutation.isPending}>
              {mutation.isPending ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : null}
              {editBranch ? "تحديث" : "إضافة"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BranchesSection;
